import {
  CONTACT_METHOD_OPTIONS,
  MULTI,
  VALUELESS,
  emptyGroup,
  newContactNode,
  newDoorVisitNode,
  newFieldNode,
  newKnockAgainGroup,
  newParticipationNode,
  newSurveyNode,
  type BuilderNode,
  type ContactNode,
  type DispositionMeta,
  type DoorVisitNode,
  type FieldMeta,
  type FieldNode,
  type GroupNode,
  type ParticipationNode,
  type SurveyNode,
  type SurveyQuestionMeta,
} from './model.ts'
import { ValueInput } from './ValueInput.tsx'

/**
 * The recursive filter editor.
 *
 * Nesting is drawn with an indent and a left rule rather than brackets: an
 * AND inside an OR is the thing people get wrong when cutting a universe, and
 * the shape has to be visible without reading it as an expression.
 */

const OP_LABELS: Record<string, string> = {
  eq: 'is',
  ne: 'is not',
  in: 'is any of',
  not_in: 'is none of',
  lt: 'is before/less than',
  lte: 'is at most',
  gt: 'is after/greater than',
  gte: 'is at least',
  between: 'is between',
  is_null: 'is missing',
  is_not_null: 'is present',
  contains: 'contains',
  starts_with: 'starts with',
}

const selectClass =
  'rounded-md border border-line bg-surface px-2 py-1.5 text-sm text-ink ' +
  'outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/25'

const addButtonClass =
  'rounded-md border border-line px-2 py-1 text-xs font-medium text-ink-muted ' +
  'hover:border-brand-400 hover:text-ink'

export function FilterGroup({
  node,
  fields,
  dispositions,
  surveyQuestions,
  depth = 0,
  onChange,
  onRemove,
}: {
  node: GroupNode
  fields: FieldMeta[]
  /** The canonical outcome vocabulary, for contact rows. */
  dispositions: DispositionMeta[]
  /** The campaign's filterable survey questions, for survey rows. */
  surveyQuestions: SurveyQuestionMeta[]
  depth?: number
  onChange: (next: GroupNode) => void
  onRemove?: () => void
}) {
  function replaceChild(index: number, next: BuilderNode | null) {
    const children = [...node.children]
    if (next === null) children.splice(index, 1)
    else children[index] = next
    onChange({ ...node, children })
  }

  return (
    <div
      className={
        depth === 0
          ? ''
          : 'rounded-md border-l-2 border-brand-300 bg-surface-sunken/60 pl-3 dark:border-brand-600'
      }
    >
      <div className="flex flex-wrap items-center gap-2 py-2">
        <select
          aria-label="Match type"
          className={selectClass}
          value={node.op}
          onChange={(e) => onChange({ ...node, op: e.target.value as 'and' | 'or' })}
        >
          <option value="and">Match all of</option>
          <option value="or">Match any of</option>
        </select>

        <label className="flex items-center gap-1.5 text-xs text-ink-muted">
          <input
            type="checkbox"
            checked={node.negated}
            onChange={(e) => onChange({ ...node, negated: e.target.checked })}
          />
          Invert
        </label>

        {onRemove ? (
          <button type="button" onClick={onRemove} className="ml-auto text-xs text-ink-muted hover:text-ink">
            Remove group
          </button>
        ) : null}
      </div>

      <div className="space-y-2">
        {node.children.map((child, index) =>
          child.kind === 'group' ? (
            <FilterGroup
              key={child.id}
              node={child}
              fields={fields}
              dispositions={dispositions}
              surveyQuestions={surveyQuestions}
              depth={depth + 1}
              onChange={(next) => replaceChild(index, next)}
              onRemove={() => replaceChild(index, null)}
            />
          ) : child.kind === 'contact' ? (
            <ContactRow
              key={child.id}
              node={child}
              dispositions={dispositions}
              onChange={(next) => replaceChild(index, next)}
              onRemove={() => replaceChild(index, null)}
            />
          ) : child.kind === 'survey' ? (
            <SurveyRow
              key={child.id}
              node={child}
              questions={surveyQuestions}
              onChange={(next) => replaceChild(index, next)}
              onRemove={() => replaceChild(index, null)}
            />
          ) : child.kind === 'door_visit' ? (
            <DoorVisitRow
              key={child.id}
              node={child}
              onChange={(next) => replaceChild(index, next)}
              onRemove={() => replaceChild(index, null)}
            />
          ) : child.kind === 'participation' ? (
            <ParticipationRow
              key={child.id}
              node={child}
              onChange={(next) => replaceChild(index, next)}
              onRemove={() => replaceChild(index, null)}
            />
          ) : (
            <ConditionRow
              key={child.id}
              node={child}
              fields={fields}
              onChange={(next) => replaceChild(index, next)}
              onRemove={() => replaceChild(index, null)}
            />
          ),
        )}
      </div>

      <div className="flex flex-wrap gap-2 py-2">
        <button
          type="button"
          className={addButtonClass}
          onClick={() =>
            fields[0] && onChange({ ...node, children: [...node.children, newFieldNode(fields[0])] })
          }
        >
          + Condition
        </button>
        <button
          type="button"
          className={addButtonClass}
          onClick={() => onChange({ ...node, children: [...node.children, newParticipationNode()] })}
        >
          + Vote history
        </button>
        <button
          type="button"
          className={addButtonClass}
          onClick={() => onChange({ ...node, children: [...node.children, newContactNode()] })}
        >
          + Contact history
        </button>
        <button
          type="button"
          className={addButtonClass}
          onClick={() => onChange({ ...node, children: [...node.children, newDoorVisitNode()] })}
        >
          + Door visits
        </button>
        <button
          type="button"
          className={addButtonClass}
          title="Doors that were not home, where nobody has been reached since"
          onClick={() => onChange({ ...node, children: [...node.children, newKnockAgainGroup()] })}
        >
          + Knock again
        </button>
        {surveyQuestions.length > 0 ? (
          <button
            type="button"
            className={addButtonClass}
            onClick={() =>
              onChange({ ...node, children: [...node.children, newSurveyNode(surveyQuestions[0])] })
            }
          >
            + Survey answer
          </button>
        ) : null}
        <button
          type="button"
          className={addButtonClass}
          onClick={() => onChange({ ...node, children: [...node.children, emptyGroup('or')] })}
        >
          + Group
        </button>
      </div>
    </div>
  )
}

function ConditionRow({
  node,
  fields,
  onChange,
  onRemove,
}: {
  node: FieldNode
  fields: FieldMeta[]
  onChange: (next: FieldNode) => void
  onRemove: () => void
}) {
  const field = fields.find((f) => f.name === node.field) ?? fields[0]
  if (!field) return null

  return (
    <div className="flex flex-wrap items-start gap-2 rounded-md border border-line bg-surface-raised p-2">
      <select
        aria-label="Field"
        className={selectClass}
        value={node.field}
        onChange={(e) => {
          const next = fields.find((f) => f.name === e.target.value)
          // Changing the field can invalidate both the operator and the value,
          // so the row is rebuilt rather than patched.
          if (next) onChange({ ...newFieldNode(next), id: node.id })
        }}
      >
        {fields.map((f) => (
          <option key={f.name} value={f.name}>
            {f.label}
          </option>
        ))}
      </select>

      <select
        aria-label="Operator"
        className={selectClass}
        value={node.cmp}
        onChange={(e) => {
          // Narrowed against the field's own operator list rather than cast: a
          // select yields a plain string, and the registry is the authority on
          // which operators this field actually accepts.
          const cmp = field.ops.find((op) => op === e.target.value)
          if (!cmp) return
          const needsList = MULTI.has(cmp) || cmp === 'between'
          onChange({
            ...node,
            cmp,
            value: VALUELESS.has(cmp) ? null : needsList ? [] : Array.isArray(node.value) ? null : node.value,
          })
        }}
      >
        {field.ops.map((op) => (
          <option key={op} value={op}>
            {OP_LABELS[op] ?? op}
          </option>
        ))}
      </select>

      <div className="min-w-[12rem] flex-1">
        <ValueInput node={node} field={field} onChange={(value) => onChange({ ...node, value })} />
      </div>

      <button type="button" onClick={onRemove} aria-label="Remove condition" className="px-1 text-ink-muted hover:text-ink">
        ×
      </button>
    </div>
  )
}

/**
 * Vote history gets its own row rather than being a field.
 *
 * It is not a column comparison — it is a popcount over the participation
 * bitmask — and phrasing it as "voted in at least 2 of the last 4 general
 * elections" is how campaigns actually describe it.
 */
function ParticipationRow({
  node,
  onChange,
  onRemove,
}: {
  node: ParticipationNode
  onChange: (next: ParticipationNode) => void
  onRemove: () => void
}) {
  const needsCount = node.mode !== 'none' && node.mode !== 'all'

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-line bg-surface-raised p-2">
      <span className="text-sm font-medium text-ink">Voted in</span>

      <select
        aria-label="Mode"
        className={selectClass}
        value={node.mode}
        onChange={(e) => onChange({ ...node, mode: e.target.value as ParticipationNode['mode'] })}
      >
        <option value="at_least">at least</option>
        <option value="at_most">at most</option>
        <option value="exactly">exactly</option>
        <option value="all">all</option>
        <option value="none">none</option>
      </select>

      {needsCount ? (
        <input
          aria-label="How many"
          type="number"
          min={0}
          max={node.recentCount}
          className={`${selectClass} w-16`}
          value={node.count}
          onChange={(e) => onChange({ ...node, count: Number(e.target.value) })}
        />
      ) : null}

      <span className="text-sm text-ink">of the last</span>

      <input
        aria-label="How many elections"
        type="number"
        min={1}
        max={40}
        className={`${selectClass} w-16`}
        value={node.recentCount}
        onChange={(e) => {
          const recentCount = Math.max(1, Number(e.target.value))
          // Asking for more than exist is rejected by the compiler, so keep the
          // threshold inside the window rather than letting it become an error.
          onChange({ ...node, recentCount, count: Math.min(node.count, recentCount) })
        }}
      />

      <select
        aria-label="Election type"
        className={selectClass}
        value={node.electionType}
        onChange={(e) => onChange({ ...node, electionType: e.target.value })}
      >
        <option value="GEN">general elections</option>
        <option value="PRI">primaries</option>
        <option value="SPE">special elections</option>
      </select>

      <button type="button" onClick={onRemove} aria-label="Remove condition" className="ml-auto px-1 text-ink-muted hover:text-ink">
        ×
      </button>
    </div>
  )
}

/**
 * "Their door was visited and nobody answered" — as a sentence, like the
 * contact row. Tested per door: every voter behind it matches (0093).
 */
function DoorVisitRow({
  node,
  onChange,
  onRemove,
}: {
  node: DoorVisitNode
  onChange: (next: DoorVisitNode) => void
  onRemove: () => void
}) {
  const which = node.outcomes.length === 1 ? node.outcomes[0] : 'either'
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-line bg-surface-raised p-2">
      <span className="text-sm font-medium text-ink">Door was</span>
      <select
        aria-label="Door outcome"
        className={selectClass}
        value={which}
        onChange={(e) =>
          onChange({
            ...node,
            outcomes: e.target.value === 'either' ? [] : [e.target.value as 'not_home' | 'inaccessible'],
          })
        }
      >
        <option value="not_home">not home</option>
        <option value="inaccessible">could not access</option>
        <option value="either">not home or could not access</option>
      </select>
      <span className="text-sm text-ink">at least</span>
      <input
        aria-label="How many times"
        type="number"
        min={1}
        max={20}
        className={`${selectClass} w-16`}
        value={node.minTimes}
        onChange={(e) => onChange({ ...node, minTimes: Math.min(20, Math.max(1, Number(e.target.value) || 1)) })}
      />
      <span className="text-sm text-ink">{node.minTimes === 1 ? 'time' : 'times'}</span>
      <span className="text-sm text-ink">since</span>
      <input
        aria-label="Since"
        type="date"
        className={selectClass}
        value={node.since}
        onChange={(e) => onChange({ ...node, since: e.target.value })}
      />
      <button type="button" onClick={onRemove} aria-label="Remove condition" className="ml-auto px-1 text-ink-muted hover:text-ink">
        ×
      </button>
    </div>
  )
}

/**
 * A condition on what the campaign heard back.
 *
 * Written as a sentence — "who said Supporter or better, by Canvass or Phone"
 * — because the thing consultants get wrong is not the syntax, it is which of
 * several similar questions they are asking. "We knocked the door" and "we
 * spoke to somebody" and "they said yes" are three different universes, and a
 * form of dropdowns lets them blur together.
 */
function ContactRow({
  node,
  dispositions,
  onChange,
  onRemove,
}: {
  node: ContactNode
  dispositions: DispositionMeta[]
  onChange: (next: ContactNode) => void
  onRemove: () => void
}) {
  const set = (patch: Partial<ContactNode>) => onChange({ ...node, ...patch })

  function toggle(list: string[], value: string): string[] {
    return list.includes(value) ? list.filter((v) => v !== value) : [...list, value]
  }

  return (
    <div className="rounded-md border border-line bg-surface-sunken/60 px-3 py-2">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-ink-muted">Contact history —</span>

        <select
          className={selectClass}
          value={node.by}
          onChange={(e) => set({ by: e.target.value as ContactNode['by'] })}
        >
          <option value="support">said</option>
          <option value="disposition">was marked</option>
          <option value="reached">was actually reached</option>
          <option value="any">has any contact recorded</option>
        </select>

        {node.by === 'support' ? (
          <>
            <label className="flex items-center gap-1 text-xs text-ink-muted">
              <input
                type="checkbox"
                checked={node.useMin}
                onChange={(e) => set({ useMin: e.target.checked })}
              />
              at least
            </label>
            <select
              className={selectClass}
              disabled={!node.useMin}
              value={node.min}
              onChange={(e) => set({ min: Number(e.target.value) })}
            >
              {SUPPORT_LEVELS.map((l) => (
                <option key={l.value} value={l.value}>{l.label}</option>
              ))}
            </select>

            <label className="flex items-center gap-1 text-xs text-ink-muted">
              <input
                type="checkbox"
                checked={node.useMax}
                onChange={(e) => set({ useMax: e.target.checked })}
              />
              at most
            </label>
            <select
              className={selectClass}
              disabled={!node.useMax}
              value={node.max}
              onChange={(e) => set({ max: Number(e.target.value) })}
            >
              {SUPPORT_LEVELS.map((l) => (
                <option key={l.value} value={l.value}>{l.label}</option>
              ))}
            </select>
          </>
        ) : null}

        <button type="button" onClick={onRemove} className="ml-auto text-xs text-ink-muted hover:text-ink">
          Remove
        </button>
      </div>

      {node.by === 'disposition' ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {dispositions.map((d) => (
            <label
              key={d.code}
              className={
                node.codes.includes(d.code)
                  ? 'cursor-pointer rounded-full border border-brand-500 bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-900'
                  : 'cursor-pointer rounded-full border border-line px-2 py-0.5 text-xs text-ink-muted hover:border-brand-400'
              }
            >
              <input
                type="checkbox"
                className="sr-only"
                checked={node.codes.includes(d.code)}
                onChange={() => set({ codes: toggle(node.codes, d.code) })}
              />
              {d.label}
            </label>
          ))}
        </div>
      ) : null}

      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-ink-muted">
        <span>by</span>
        {CONTACT_METHOD_OPTIONS.map((m) => (
          <label
            key={m.value}
            className={
              node.methods.includes(m.value)
                ? 'cursor-pointer rounded-full border border-brand-500 bg-brand-50 px-2 py-0.5 font-medium text-brand-900'
                : 'cursor-pointer rounded-full border border-line px-2 py-0.5 hover:border-brand-400'
            }
          >
            <input
              type="checkbox"
              className="sr-only"
              checked={node.methods.includes(m.value)}
              onChange={() => set({ methods: toggle(node.methods, m.value) })}
            />
            {m.label}
          </label>
        ))}
        {node.methods.length === 0 ? <span>(any method)</span> : null}

        <span className="ml-2">using</span>
        <select
          className={selectClass}
          value={node.scope}
          onChange={(e) => set({ scope: e.target.value as ContactNode['scope'] })}
        >
          {/* The distinction that matters: someone marked undecided on Tuesday
              and supportive on Saturday is a supporter under 'latest' and both
              under 'any'. */}
          <option value="latest">their most recent answer</option>
          <option value="any">any answer they gave</option>
        </select>

        <span className="ml-2">since</span>
        <input
          type="date"
          className={selectClass}
          value={node.since}
          onChange={(e) => set({ since: e.target.value })}
        />
        <span>before</span>
        <input
          type="date"
          className={selectClass}
          value={node.before}
          onChange={(e) => set({ before: e.target.value })}
        />
      </div>

      {node.by === 'reached' ? (
        <p className="mt-2 text-xs text-ink-muted">
          Reached means somebody answered. A not-home or no-answer is an attempt, and does not
          count here.
        </p>
      ) : null}
    </div>
  )
}

/**
 * The support scale, in a campaign's words rather than as numbers.
 *
 * The stored value is -2…+2 on campaign.disposition, which is what makes "said
 * yes" answerable across campaigns whose printed tokens all differ. Nobody
 * building a universe should have to know that.
 */
/**
 * A condition on what a voter said on a survey. Reads as a sentence like the
 * contact row — "Survey answer — support chose Strong yes" — for the same
 * reason: which question is being asked matters more than the syntax.
 */
function SurveyRow({
  node,
  questions,
  onChange,
  onRemove,
}: {
  node: SurveyNode
  questions: SurveyQuestionMeta[]
  onChange: (next: SurveyNode) => void
  onRemove: () => void
}) {
  const set = (patch: Partial<SurveyNode>) => onChange({ ...node, ...patch })
  const current = questions.find((q) => q.question_code === node.question) ?? questions[0]
  const answers = current?.answers ?? []

  function toggle(list: string[], value: string): string[] {
    return list.includes(value) ? list.filter((v) => v !== value) : [...list, value]
  }

  return (
    <div className="rounded-md border border-line bg-surface-sunken/60 px-3 py-2">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-ink-muted">Survey answer —</span>

        <select
          className={selectClass}
          value={node.question}
          onChange={(e) => set({ question: e.target.value, codes: [] })}
        >
          {questions.map((q) => (
            <option key={q.question_code} value={q.question_code}>
              {q.prompt || q.question_code}
            </option>
          ))}
        </select>

        <select
          className={selectClass}
          value={node.by}
          onChange={(e) => set({ by: e.target.value as SurveyNode['by'] })}
        >
          <option value="answered">was answered</option>
          {answers.length > 0 ? <option value="answer">chose</option> : null}
          <option value="value">has a value</option>
        </select>

        {node.by === 'value' ? (
          <>
            <label className="flex items-center gap-1 text-xs text-ink-muted">
              <input type="checkbox" checked={node.useMin} onChange={(e) => set({ useMin: e.target.checked })} />
              at least
            </label>
            <input
              type="number"
              className={`${selectClass} w-20`}
              disabled={!node.useMin}
              value={node.min}
              onChange={(e) => set({ min: Number(e.target.value) })}
            />
            <label className="flex items-center gap-1 text-xs text-ink-muted">
              <input type="checkbox" checked={node.useMax} onChange={(e) => set({ useMax: e.target.checked })} />
              at most
            </label>
            <input
              type="number"
              className={`${selectClass} w-20`}
              disabled={!node.useMax}
              value={node.max}
              onChange={(e) => set({ max: Number(e.target.value) })}
            />
          </>
        ) : null}

        <select
          className={selectClass}
          value={node.scope}
          onChange={(e) => set({ scope: e.target.value as SurveyNode['scope'] })}
          title="Which answer counts"
        >
          <option value="latest">latest answer</option>
          <option value="any">any time</option>
        </select>

        <button type="button" onClick={onRemove} className="ml-auto text-xs text-ink-muted hover:text-ink">
          Remove
        </button>
      </div>

      {node.by === 'answer' ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {answers.map((a) => (
            <label
              key={a.code}
              className={
                node.codes.includes(a.code)
                  ? 'cursor-pointer rounded-full border border-brand-500 bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-900'
                  : 'cursor-pointer rounded-full border border-line px-2 py-0.5 text-xs text-ink-muted hover:border-brand-400'
              }
            >
              <input
                type="checkbox"
                className="sr-only"
                checked={node.codes.includes(a.code)}
                onChange={() => set({ codes: toggle(node.codes, a.code) })}
              />
              {a.label || a.code}
            </label>
          ))}
          {answers.length === 0 ? <span className="text-xs text-ink-muted">no answer options</span> : null}
        </div>
      ) : null}
    </div>
  )
}

const SUPPORT_LEVELS = [
  { value: 2, label: 'Strong supporter' },
  { value: 1, label: 'Leaning support' },
  { value: 0, label: 'Undecided' },
  { value: -1, label: 'Leaning opposed' },
  { value: -2, label: 'Strongly opposed' },
]
